/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injector, resolveForwardRef } from '@angular/core';
import { environment } from '@env/environment';
import { InjectionTokens } from '@src/serviceTokens';

// Returns a factory function that returns a service for the current site when given a map of services.
// The map provided must be a map of services that are injectable.
export function siteService(services: { [site: string]: any }): (i: Injector) => any {
  return (injector: Injector) => {
    const token = resolveForwardRef(services[environment.site]);
    return injector.get(token);
  };
}

// Returns a resource for the current site when given a map of resources
export function siteResource(resources: { [site: string]: any }): any {
  return resources[environment.site];
}

/**
 * Registers a site-specific service implementation for use in main.ts.
 *
 * Returns an array so main.ts can spread it into the providers list (e.g.
 * `...serviceProvider(LoginService, { lausd: LausdLoginService, ca: LoginService })`).
 * A single array is returned so both providers are registered together and the
 * caller does not have to repeat the token/class mapping.
 *
 * Two providers are required:
 *
 * 1. Token -> useClass(site implementation)
 *    Binds the injection token to the implementation for the current site
 *    (e.g. LausdLoginService when site is lausd). Only this implementation
 *    is instantiated, so only one of the site implementations is loaded.
 *
 * 2. defaultService (e.g. LoginService) -> useExisting(token)
 *    Aliases the concrete/abstract class to the token so code that injects
 *    by class (e.g. inject(LoginService) or constructor(private login: LoginService))
 *    receives the same single instance from (1) instead of creating a second
 *    instance. Without this alias, class-based injection would instantiate the
 *    default class and both site implementations would be loaded.
 *
 * Add a token for the service in serviceTokens.ts and inject via that token
 * where you need the site-specific implementation.
 */
export function serviceProvider(defaultService: any, services: { [site: string]: any }) {
  return [
    {
      provide: InjectionTokens.get(defaultService),
      useClass: siteResource(services),
    },
    {
      provide: defaultService,
      useExisting: InjectionTokens.get(defaultService),
    },
  ];
}
